<template>
  <b-modal
    v-model="isOpenModel"
    width="520px"
    scroll="keep"
    :hide-header="true"
    :hide-footer="true"
    class="reporting-objects-modal">
    <div class="reporting-objects-card">
      <div class="modal-header">
        <h3 class="modal-title">
          Generate Reporting Objects
        </h3>
        <button
          class="button is-light close-button"
          @click="close">
          <b-icon icon="close" />
        </button>
      </div>
      <div class="modal-content">
        <p class="modal-subtitle">
          For this attribute, create the following reporting objects:
        </p>
        <div class="reporting-option">
          <b-checkbox
            v-model="createExpressionModel"
            :disabled="createDimensionModel">
            Expression
          </b-checkbox>
          <div
            v-if="showExpressionWarning"
            class="warning-text">
            An expression already exists for this attribute:
            <a @click="openExpressionLink">
              {{ primaryExpressionName }}
            </a>
            <span v-if="usageCount > 1">
              and {{ otherUsageCount }} other{{ otherUsageCount === 1 ? '' : 's' }}.
            </span>
            <button
              v-if="usageCount > 1"
              class="usage-link"
              @click="$emit('view-usage', attributeUsages)">
              View all usage
            </button>
          </div>
        </div>
        <div class="reporting-option">
          <b-checkbox
            v-model="createDimensionModel"
            :disabled="!canAutoCreateDefinitions">
            Dimension
          </b-checkbox>
          <div
            v-if="showDimensionWarning"
            class="warning-text">
            A dimension already exists for this attribute:
            <a @click="openDimensionLink">
              {{ primaryDimensionName }}
            </a>
            <span v-if="dimensionUsageCount > 1">
              and {{ otherDimensionUsageCount }} other{{ otherDimensionUsageCount === 1 ? '' : 's' }}.
            </span>
            <button
              v-if="dimensionUsageCount > 1"
              class="usage-link"
              @click="$emit('view-usage', attributeUsages)">
              View all usage
            </button>
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <b-button
          type="is-light"
          @click="close">
          Cancel
        </b-button>
        <b-button
          type="is-primary"
          @click="submit">
          Create
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import { openInParent } from '@/utils/parentNav'

  export default {
    name: 'GenerateReportingObjectsModal',
    props: {
      isOpen: { type: Boolean, default: false, },
      createExpression: { type: Boolean, default: false, },
      createDimension: { type: Boolean, default: false, },
      canAutoCreateDefinitions: { type: Boolean, default: false, },
      attributeId: { type: [Number, String,], default: null, },
    },
    data () {
      return {
        directExpressionUsages: [],
        attributeUsages: null,
        usageLoading: false,
      }
    },
    computed: {
      isOpenModel: {
        get () {
          return this.isOpen
        },
        set (value) {
          this.$emit('update:isOpen', value)
        },
      },
      createExpressionModel: {
        get () {
          return this.createExpression
        },
        set (value) {
          this.$emit('update:createExpression', value)
        },
      },
      createDimensionModel: {
        get () {
          return this.createDimension
        },
        set (value) {
          this.$emit('update:createDimension', value)
        },
      },
      showExpressionWarning () {
        return this.createExpressionModel && this.usageCount > 0
      },
      usageCount () {
        return this.directExpressionUsages.length
      },
      otherUsageCount () {
        return Math.max(this.usageCount - 1, 0)
      },
      primaryUsage () {
        return this.directExpressionUsages[0]
      },
      primaryExpressionName () {
        return this.primaryUsage?.referencer_name
      },
      primaryExpressionLink () {
        const companyId = this.$route.params.company_id
        const id = this.primaryUsage?.referencer_id
        return `/company/${companyId}/tools/expressions?id=${id}`
      },
      dimensionUsages () {
        return (this.attributeUsages || []).filter(
          usage => usage.referencer_type === 'COMPANY_DIMENSION'
        )
      },
      dimensionUsageCount () {
        return this.dimensionUsages.length
      },
      otherDimensionUsageCount () {
        return Math.max(this.dimensionUsageCount - 1, 0)
      },
      primaryDimensionUsage () {
        return this.dimensionUsages[0]
      },
      primaryDimensionName () {
        return this.primaryDimensionUsage?.referencer_name
      },
      primaryDimensionLink () {
        const companyId = this.$route.params.company_id
        const key = this.primaryDimensionUsage?.key || this.primaryDimensionUsage?.referencer_id
        return `/company/${companyId}/tools/reports/config?tab=companyDimension&key=${key}`
      },
      showDimensionWarning () {
        return this.createDimensionModel && this.dimensionUsageCount > 0
      },
    },
    watch: {
      canAutoCreateDefinitions (value) {
        // If user loses permission, force Dimension off.
        if (!value) {
          this.createDimensionModel = false
        }
      },
      isOpenModel (value) {
        // Prefetch usage when the modal opens.
        if (value) {
          this.loadAttributeUsage()
        }
      },
      createDimensionModel (value) {
        // Dimension implies Expression.
        if (value) {
          this.createExpressionModel = true
          if (!this.attributeUsages) {
            this.loadAttributeUsage()
          }
        }
      },
      createExpressionModel (value) {
        // Ensure usage is loaded when Expression is checked.
        if (value) {
          if (!this.attributeUsages) {
            this.loadAttributeUsage()
          }
        }
      },
      attributeId () {
        // Reset cached usage when we change attributes.
        this.directExpressionUsages = []
        this.attributeUsages = null
      },
    },
    methods: {
      openExpressionLink () {
        openInParent(this.primaryExpressionLink)
      },
      openDimensionLink () {
        openInParent(this.primaryDimensionLink)
      },
      close () {
        this.isOpenModel = false
      },
      submit () {
        this.isOpenModel = false
        this.$emit('submit')
      },
      async loadAttributeUsage () {
        // Load usage once; reuse the same response for warnings and links.
        if (this.usageLoading) {
          return
        }
        this.usageLoading = true
        try {
          const response = await this.$apis.expressions.getAttributeUsage(
            this.attributeId
          )
          this.attributeUsages = response
          this.directExpressionUsages = this.attributeUsages.filter(
            usage =>
              usage.referencer_type === 'EXPRESSION' &&
              usage.path.length === 0
          )
        } finally {
          this.usageLoading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.reporting-objects-card {
  background: white;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .modal-title {
      font-size: 18px;
      font-weight: 600;
    }
  }

  .modal-content {
    padding: 8px 0 0 0;
  }

  .modal-subtitle {
    color: $grayscale-4;
    margin-bottom: 14px;
  }

  .reporting-option {
    padding: 10px 12px;
    border: $border-1;
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .warning-text {
    margin-top: 8px;
    color: #6b7280;
    font-size: 13px;
  }

  .usage-link {
    margin-left: 6px;
    padding: 0;
    border: none;
    background: transparent;
    color: #6b7280;
    text-decoration: underline;
    cursor: pointer;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
}
</style>
